import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Chip, Grid, Paper, Stack, Typography } from "@mui/material";
import OndemandVideoRoundedIcon from "@mui/icons-material/OndemandVideoRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import VideoGalleryTableComponent from "../../../components/admin/video-gallery-manager/VideoGalleryTable";
import { VideoGalleryAddDrawer } from "../../../components/admin/video-gallery-manager/VideoGalleryAddDrawer";
import { PhotoGalleryAssignDialog } from "../../../components/admin/photo-gallery-manager/PhotoGalleryAssignDialog";
import { SearchRecords } from "../../../components/Common/SearchRecords";
import AutoCompleteSelect from "../../../components/Common/AutoCompleteSelect";
import { useGetGalleryFolderListQuery, useGetGalleryListQuery } from "../../../store/masters/mastersApis";
import { defaultPaginate, PERMISSIONS } from "../../../helpers/constants";
import HasPermission from "../../../components/Common/HasPermission";

const VideoGalleryManagerContainer = (props) => {
    const {
        showDrawer,
        formType,
        initialValues,
        pagination,
        handlePaginationState,
        changeMastersInitialState,
        navigate,
    } = props;
    const [assignDialog, setAssignDialog] = useState({
        show: false,
        initialValues: { _id: "", title: "", folder_id: "" },
    });

    const { isLoading, data: gallery, isFetching } = useGetGalleryListQuery({
        ...pagination,
        type: "video",
    });
    const { data: folderResponse } = useGetGalleryFolderListQuery({
        pageNo: 0,
        limit: 500,
        sortBy: 1,
        sortField: "name",
    });

    useEffect(() => {
        handlePaginationState({
            ...defaultPaginate,
            folder_id: "",
        });
    }, [handlePaginationState]);

    const folderOptions = useMemo(
        () =>
            (folderResponse?.result || []).map((folder) => ({
                value: folder._id,
                label: folder.name,
                description: folder.description,
                activityLabel: folder?.activity_data?.name || "",
            })),
        [folderResponse],
    );
    const selectedFolder =
        folderOptions.find((folder) => folder.value === pagination?.folder_id) || null;
    const unassignedVideoCount = (gallery?.result || []).filter((item) => !item?.folder_data?.name).length;

    const handlePagination = (setting) => {
        handlePaginationState(setting);
    };

    const handleAddVideoGallery = () => {
        changeMastersInitialState({
            showDrawer: true,
            formType: "Add",
            initialValues: {
                folder_id: pagination?.folder_id || "",
                url: "",
                video_thumbnail: "",
            },
        });
    };

    const handleDrawerClose = () => {
        changeMastersInitialState({
            showDrawer: false,
            formType: "",
            initialValues: { folder_id: "", url: "", video_thumbnail: "" },
        });
    };

    const handleFolderFilterChange = (_, value) => {
        handlePaginationState({
            ...pagination,
            pageNo: 0,
            folder_id: value?.value || "",
        });
    };

    const openAssignDialog = (video) => {
        setAssignDialog({
            show: true,
            initialValues: {
                _id: video?._id || "",
                title: video?.title || "",
                folder_id: video?.folder_id || video?.folder_data?._id || "",
            },
        });
    };

    const closeAssignDialog = () => {
        setAssignDialog({
            show: false,
            initialValues: { _id: "", title: "", folder_id: "" },
        });
    };

    return (
        <Stack spacing={2}>
            <Paper
                sx={{
                    p: 3,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, rgba(14,93,170,0.08) 0%, rgba(7,27,42,0.02) 100%)",
                }}
            >
                <Stack spacing={3}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Stack spacing={1}>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    Video Gallery Management
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Manage video records against the same activity-linked folder library used
                                    for photos, so both media types stay organized together.
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.5}
                                justifyContent="flex-end"
                                alignItems={{ xs: "stretch", sm: "center" }}
                            >
                                <HasPermission
                                    permissions={[
                                        PERMISSIONS.COMMON_MASTER.PHOTO_GALLERY.CREATE,
                                        PERMISSIONS.COMMON_MASTER.VIDEO_GALLERY.CREATE,
                                    ]}
                                    fallback={null}
                                >
                                    <Button
                                        disableElevation
                                        variant="outlined"
                                        startIcon={<SettingsRoundedIcon />}
                                        sx={{ borderRadius: "14px", minWidth: 160 }}
                                        onClick={() => navigate("/gallery-folder-master")}
                                    >
                                        Manage Folders
                                    </Button>
                                </HasPermission>
                                <HasPermission permission={PERMISSIONS.COMMON_MASTER.VIDEO_GALLERY.CREATE} fallback={null}>
                                    <Button
                                        disableElevation
                                        variant="contained"
                                        startIcon={<OndemandVideoRoundedIcon />}
                                        sx={{ borderRadius: "14px", minWidth: 170 }}
                                        onClick={handleAddVideoGallery}
                                    >
                                        Add Video
                                    </Button>
                                </HasPermission>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "rgba(255,255,255,0.72)",
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <SearchRecords handlePagination={handlePagination} pagination={pagination} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <AutoCompleteSelect
                                    id="video-gallery-folder-filter"
                                    options={folderOptions}
                                    label="Filter by Folder"
                                    onChange={handleFolderFilterChange}
                                    value={selectedFolder}
                                    name="folder_id"
                                    keyname="label"
                                    disableCloseOnSelect={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                                    flexWrap="wrap"
                                    useFlexGap
                                >
                                    <Chip
                                        color={selectedFolder ? "primary" : "default"}
                                        label={
                                            selectedFolder
                                                ? `Folder: ${selectedFolder.label}`
                                                : "Showing all folders"
                                        }
                                        sx={{ borderRadius: "10px" }}
                                    />
                                    {selectedFolder?.activityLabel ? (
                                        <Chip
                                            variant="outlined"
                                            label={`Activity: ${selectedFolder.activityLabel}`}
                                            sx={{ borderRadius: "10px" }}
                                        />
                                    ) : null}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Stack>
            </Paper>

            <Paper sx={{ p: 2.5, color: "#071B2A", fontWeight: "400", borderRadius: 3 }} elevation={0}>
                <Grid container justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={0.5}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Video Records
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip
                                    size="small"
                                    color={selectedFolder ? "primary" : "default"}
                                    label={selectedFolder ? selectedFolder.label : "All folders"}
                                    sx={{ borderRadius: "8px" }}
                                />
                                {unassignedVideoCount > 0 ? (
                                    <Chip
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        label={`${unassignedVideoCount} unassigned on this page`}
                                        sx={{ borderRadius: "8px" }}
                                    />
                                ) : null}
                            </Stack>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" textAlign={{ xs: "left", md: "right" }}>
                            Videos now use the same shared folder library as photos.
                        </Typography>
                    </Grid>
                </Grid>
                {(folderResponse?.count || 0) === 0 ? (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        No folders exist yet. Create a folder in Folder Management before adding videos.
                    </Alert>
                ) : null}
                {unassignedVideoCount > 0 ? (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        Some older videos on this page are still unassigned to a folder.
                    </Alert>
                ) : null}
                <VideoGalleryTableComponent
                    fetching={isFetching}
                    loading={isLoading}
                    count={gallery?.count || 0}
                    data={gallery?.result || []}
                    pagination={pagination}
                    handlePagination={handlePagination}
                    onAssignFolder={openAssignDialog}
                />
            </Paper>

            <VideoGalleryAddDrawer
                show={showDrawer}
                close={handleDrawerClose}
                formType={formType}
                initialValues={initialValues}
            />
            <PhotoGalleryAssignDialog
                show={assignDialog.show}
                close={closeAssignDialog}
                initialValues={assignDialog.initialValues}
                folderOptions={folderOptions}
            />
        </Stack>
    );
};

export default VideoGalleryManagerContainer;
